import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mic, Briefcase, Settings, Zap, Trophy, Star, TrendingUp, Bot } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/DashboardLayout";
import { CreditConfirmDialog } from "@/components/CreditConfirmDialog";
import { toast } from "sonner";
import { hashContent } from "@/lib/creditGuard";

const AIInterviewPrep = () => {
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const [stats, setStats] = useState({
    totalInterviews: 0,
    avgScore: 0,
    improvement: 0
  });
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMode, setConfirmMode] = useState<'quick' | 'job' | 'custom'>('quick');
  const [isCharging, setIsCharging] = useState(false);

  useEffect(() => {
    loadUserProgress();
  }, []);

  const loadUserProgress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: progress } = await supabase
        .from('interview_progress')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (progress) {
        setStats({
          totalInterviews: progress.total_interviews || 0,
          avgScore: progress.average_score || 0,
          improvement: calculateImprovement(progress)
        });
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateImprovement = (progress: any) => {
    // Simple calculation - in production, compare with previous periods
    return progress.average_score > 7 ? 15 : 5;
  };

  if (!hasFeature('aiInterviewPrep')) {
    return (
        <SubscriptionGate
          requiredPlan="premium"
          feature="AI Interview Prep"
          description="Practice unlimited interviews with AI feedback, get real-time coaching, and build confidence for any interview."
        />
    );
  }

  const handleStartQuickPractice = () => {
    setConfirmMode('quick');
    setShowConfirm(true);
  };

  const handleStartJobSpecific = () => {
    setConfirmMode('job');
    setShowConfirm(true);
  };

  const handleStartCustom = () => {
    setConfirmMode('custom');
    setShowConfirm(true);
  };

  const handleConfirmInterview = async () => {
    setIsCharging(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Generate ref_id for idempotency
      const contextData = `interview:${confirmMode}:${Date.now()}`;
      const contextHash = await hashContent(contextData);
      const refId = `interview:${confirmMode}:${contextHash}:${Date.now()}`;

      // Feature keys map to interview modes
      const featureKeyMap = {
        'quick': 'interview.quick_practice',
        'job': 'interview.job_start',
        'custom': 'interview.custom'
      };

      // Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: featureKeyMap[confirmMode], refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'PREMIUM_REQUIRED') {
          toast.error(errorData.message || 'Interview Practice requires a Premium plan', {
            action: { label: 'Upgrade', onClick: () => navigate('/pricing') },
          });
          return;
        }
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Insufficient credits', {
            action: { label: 'Get Credits', onClick: () => navigate('/pricing') },
          });
          return;
        }
        throw creditError;
      }

      // Show credit usage toast
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`${creditResult.cost} credits used. ${creditResult.remaining} remaining.`);
      }

      // Navigate to interview practice
      const pathMap = {
        'quick': '/app/ai-interview-prep/practice/quick',
        'job': '/app/ai-interview-prep/practice/job-specific',
        'custom': '/app/ai-interview-prep/practice/custom'
      };
      
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
      navigate(pathMap[confirmMode]);
      setShowConfirm(false);
    } catch (error: any) {
      console.error('[INTERVIEW-PREP] Error:', error);
      toast.error('Failed to start interview: ' + (error.message || 'Please try again'));
    } finally {
      setIsCharging(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>AI Interview Practice | Master Every Interview</title>
        <meta name="description" content="Practice unlimited interviews with our AI coach. Get real-time feedback. Build confidence." />
      </Helmet>

      <DashboardLayout>
        <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
          {/* Hero Section */}
          <section className="relative px-6 pt-12 pb-24 overflow-hidden">
            <div className="absolute inset-0 bg-[var(--gradient-glow)] opacity-30" />
            
            <div className="relative max-w-7xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Left: Illustration */}
                <div className="relative">
                  <div className="w-full aspect-square relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl animate-pulse" />
                    <div className="relative w-full h-full flex items-center justify-center">
                      <Bot className="w-64 h-64 text-primary/60" />
                    </div>
                  </div>
                </div>

                {/* Right: Hero Content */}
                <div className="space-y-8">
                  <div className="space-y-4">
                    <h1 className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-[var(--gradient-primary)]">
                      Master Every Interview
                    </h1>
                    <p className="text-xl text-muted-foreground">
                      Practice unlimited interviews with our AI coach. Get real-time feedback. Build confidence.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      size="lg" 
                      className="text-lg h-14 gap-2"
                      onClick={handleStartQuickPractice}
                    >
                      <Mic className="w-5 h-5" />
                      Start Practice Interview
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="text-lg h-14"
                      onClick={() => navigate('/app/ai-interview-prep/questions')}
                    >
                      Generate Interview Questions
                    </Button>
                  </div>

                  {/* Quick Stats Bar */}
                  <div className="grid grid-cols-3 gap-4 pt-8">
                    <Card className="bg-card/50 backdrop-blur border-primary/20">
                      <CardContent className="p-4 text-center">
                        <Trophy className="w-8 h-8 mx-auto mb-2 text-primary" />
                        <div className="text-2xl font-bold">{stats.totalInterviews}</div>
                        <div className="text-xs text-muted-foreground">Interviews Practiced</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-card/50 backdrop-blur border-primary/20">
                      <CardContent className="p-4 text-center">
                        <Star className="w-8 h-8 mx-auto mb-2 text-accent" />
                        <div className="text-2xl font-bold">{stats.avgScore.toFixed(1)}/10</div>
                        <div className="text-xs text-muted-foreground">Avg. Answer Score</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-card/50 backdrop-blur border-primary/20">
                      <CardContent className="p-4 text-center">
                        <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        <div className="text-2xl font-bold">+{stats.improvement}%</div>
                        <div className="text-xs text-muted-foreground">Improvement</div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Interview Mode Selector */}
          <section className="px-6 pb-24">
            <div className="max-w-7xl mx-auto space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-4xl font-bold">Choose Your Interview Type</h2>
                <p className="text-muted-foreground text-lg">Select the practice mode that fits your needs</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Quick Practice */}
                <Card className="relative overflow-hidden hover:border-primary/50 transition-all cursor-pointer group" onClick={handleStartQuickPractice}>
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <CardHeader>
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Zap className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Quick Practice</CardTitle>
                    <CardDescription>5-10 common questions, 15 minutes</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Best for:</strong> Daily practice, warmup</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Questions:</strong> Behavioral, general fit</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Duration:</strong> 15-20 minutes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Difficulty:</strong> Beginner to Intermediate</span>
                      </li>
                    </ul>
                    <Button className="w-full" size="lg">Start Quick Practice</Button>
                  </CardContent>
                </Card>

                {/* Job-Specific Interview */}
                <Card className="relative overflow-hidden hover:border-primary/50 transition-all cursor-pointer group" onClick={handleStartJobSpecific}>
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <CardHeader>
                    <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Briefcase className="w-8 h-8 text-accent" />
                    </div>
                    <CardTitle className="text-2xl">Job-Specific Interview</CardTitle>
                    <CardDescription>Tailored to a specific job posting</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        <span><strong>Best for:</strong> Preparing for a real interview</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        <span><strong>Questions:</strong> Role-specific, technical, behavioral</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        <span><strong>Duration:</strong> 30-45 minutes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        <span><strong>Difficulty:</strong> Matches job level</span>
                      </li>
                    </ul>
                    <Button className="w-full" size="lg" variant="secondary">Select Job & Start</Button>
                  </CardContent>
                </Card>

                {/* Custom Interview */}
                <Card className="relative overflow-hidden hover:border-primary/50 transition-all cursor-pointer group" onClick={handleStartCustom}>
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <CardHeader>
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Settings className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Custom Interview</CardTitle>
                    <CardDescription>Build your own interview</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Best for:</strong> Targeting specific skills</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Questions:</strong> You choose the topics</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Duration:</strong> Custom</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Difficulty:</strong> You set it</span>
                      </li>
                    </ul>
                    <Button className="w-full" size="lg" variant="outline">Customize Interview</Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Additional Quick Links */}
          <section className="px-6 pb-24">
            <div className="max-w-7xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="hover:border-primary/50 transition-all cursor-pointer" onClick={() => navigate('/app/ai-interview-prep/history')}>
                  <CardHeader>
                    <CardTitle>Practice History</CardTitle>
                    <CardDescription>Review your past interview sessions</CardDescription>
                  </CardHeader>
                </Card>
                <Card className="hover:border-primary/50 transition-all cursor-pointer" onClick={() => navigate('/app/ai-interview-prep/library')}>
                  <CardHeader>
                    <CardTitle>Answer Library</CardTitle>
                    <CardDescription>Your collection of practiced answers</CardDescription>
                  </CardHeader>
                </Card>
                <Card className="hover:border-primary/50 transition-all cursor-pointer" onClick={() => navigate('/app/ai-interview-prep/analytics')}>
                  <CardHeader>
                    <CardTitle>Progress Analytics</CardTitle>
                    <CardDescription>Track your improvement over time</CardDescription>
                  </CardHeader>
                </Card>
              </div>
            </div>
          </section>
        </div>
      </DashboardLayout>
      {/* Credit Confirmation Modal */}
      <CreditConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={handleConfirmInterview}
        credits={5}
        featureName={`${confirmMode === 'quick' ? 'Quick Practice' : confirmMode === 'job' ? 'Job-Specific' : 'Custom'} Interview`}
        loading={isCharging}
        isPremiumFeature={true}
      />
    </>
  );
};

export default AIInterviewPrep;